import { useNavigateBack } from '@exam-management/common/hooks';
import { useQContent } from '@exam-management/common/qcontent-provider';
import { FormLeavingListener } from '@exam-management/components';
import { QuestionGroupType } from '@exam-management/graphql';
import { APP_PERMISSIONS } from '@exam-management/permissions';
import { SingleQuestionHeader, useQContentDetailsModal } from '@exam-management/qcontent-detail';
import {
  type QContentDetailFormContextData,
  QContentDetailsSection,
  QContentLocationSection,
  QuestionLayout,
  useSaveQuestionFormSubmission,
} from '@exam-management/qcontent-management';
import { useNavigate, useParams } from '@exam-management/router';
import { useHasPermission } from '@shared/permissions';
import { useEffect } from 'react';
import { Form, useFormContext } from 'react-hook-form';

export default function QContentDetailPage() {
  const { structureId, contentId } = useParams(
    '/qbank-structures/:structureId/qcontents/single/:contentId',
  );

  const isEditable = useHasPermission(APP_PERMISSIONS.QBANK.QUESTION.UPDATE);
  const { qContent } = useQContent();
  const navigate = useNavigate();
  const formContext = useFormContext<QContentDetailFormContextData>();

  const { previewQuestion, updatePUse } = useQContentDetailsModal();
  const { handleSubmit } = useSaveQuestionFormSubmission({ contentId });
  const handleClickSave = async (data: QContentDetailFormContextData) => {
    await handleSubmit(data);
  };

  const handleClickSaveAndNew = async (data: QContentDetailFormContextData) => {
    await handleSubmit(data);
    setTimeout(
      () =>
        navigate(`/qbank-structures/:structureId/qcontents/single/create`, {
          params: { structureId },
          state: { locationFields: (data as QContentDetailFormContextData).location },
        }),
      100,
    );
  };

  const { handleBack } = useNavigateBack({
    fallbackPath: '/qbank-structures/:structureId/qcontents',
    matchPatterns: '/qbank-structures/:structureId/qcontents',
    fallbackParams: { structureId },
  });

  useEffect(() => {
    if (formContext.formState.isSubmitSuccessful) {
      formContext.reset(formContext.getValues());
    }
  }, [formContext.formState.isSubmitSuccessful]);

  useEffect(() => {
    if (qContent.questionGroup?.type === QuestionGroupType.ItemSet) {
      handleBack();
    }
  }, [qContent.questionGroup?.type, handleBack]);

  if (qContent.questionGroup?.type === QuestionGroupType.ItemSet) {
    return null;
  }

  return (
    <Form>
      <SingleQuestionHeader handleSave={handleSubmit} structureId={structureId} />
      <QuestionLayout
        sideContent={
          <QContentLocationSection
            readonly={!isEditable}
            questionStatus={qContent.status}
            questionId={contentId}
            refQBankStructureId={qContent.refQBankStructureId}
          />
        }
      >
        <QContentDetailsSection
          onSubmit={handleClickSave}
          onSubmitAndCreateNew={handleClickSaveAndNew}
          onPreviewQuestion={previewQuestion}
          questionId={contentId}
          readonly={!isEditable}
          questionStatus={qContent.status}
          onClickUpdatePUseModal={updatePUse}
        />
      </QuestionLayout>

      <FormLeavingListener onSubmit={handleSubmit} />
    </Form>
  );
}
